import { create } from 'zustand';

import { fileExtension, mimeTypeForExtension } from '@shared/lib/file-format';

import { getAttachmentPolicy } from '../data/attachment-policy';
import { UPLOAD_CONCURRENCY } from '../data/attachments';
import { getChatService } from '../service/ai-cio-service';
import type { AttachmentPolicy, AttachmentStatus, ChatService, ComposerAttachment } from '../types';

export const NEW_CHAT_DRAFT_KEY = 'new';

interface ComposerAttachmentsState {
  drafts: Record<string, ComposerAttachment[]>;
  texts: Record<string, string>;
  addFiles: (key: string, files: File[], options: { uploads: boolean }) => { dropped: number };
  setText: (key: string, text: string) => void;
  remove: (key: string, id: string) => void;
  retry: (key: string, id: string) => void;
  clear: (key: string) => void;
  take: (key: string) => ComposerAttachment[];
  move: (fromKey: string, toKey: string) => void;
}

type Upload = NonNullable<ChatService['uploadFile']>;

type UploadFailure = Pick<ComposerAttachment, 'status' | 'error' | 'refusalCode' | 'limitBytes'>;

const TRANSIENT_4XX_STATUSES: readonly number[] = [401, 408, 429];

const uploadQueue: string[] = [];
const uploadControllers = new Map<string, AbortController>();

let attachmentSeq = 0;

function nextAttachmentId(): string {
  return `att-${Date.now().toString(36)}-${(++attachmentSeq).toString(36)}`;
}

function rejectionStatus(file: File, policy: AttachmentPolicy): AttachmentStatus | undefined {
  if (!policy.extensions.includes(fileExtension(file.name))) return 'unsupported';
  if (file.size > policy.maxBytes) return 'too-large';
  return undefined;
}

function refusalCode(err: Error): string | undefined {
  return 'code' in err && typeof err.code === 'string' ? err.code : undefined;
}

function refusalLimitBytes(err: Error): number | undefined {
  if (!('limitBytes' in err)) return undefined;
  const { limitBytes } = err;
  return typeof limitBytes === 'number' && Number.isFinite(limitBytes) && limitBytes > 0
    ? limitBytes
    : undefined;
}

function uploadFailure(err: unknown): UploadFailure {
  const retryable: UploadFailure = { status: 'failed', error: undefined };
  if (!(err instanceof Error) || !('status' in err)) return retryable;
  const { status } = err;
  if (typeof status !== 'number' || status < 400 || status >= 500) return retryable;
  if (TRANSIENT_4XX_STATUSES.includes(status)) return retryable;
  const error = err.message || undefined;
  switch (refusalCode(err)) {
    case 'type_not_allowed':
      return { status: 'unsupported', error: undefined };
    case 'too_large':
      return { status: 'too-large', error: undefined, limitBytes: refusalLimitBytes(err) };
    case 'empty_file':
      return { status: 'failed', error: undefined, refusalCode: 'empty_file' };
    default:
      return { status: 'failed', error };
  }
}

function deleteUploadedRef(service: ChatService, fileRef: string): void {
  void service.deleteFile?.(fileRef).catch(() => undefined);
}

function deleteUploads(attachments: readonly ComposerAttachment[]): void {
  const service = getChatService();
  for (const { status, fileRef } of attachments) {
    if (status === 'ready' && fileRef) deleteUploadedRef(service, fileRef);
  }
}

function withoutKey<T>(record: Record<string, T>, key: string): Record<string, T> {
  return Object.fromEntries(Object.entries(record).filter(([entryKey]) => entryKey !== key));
}

function findAttachment(id: string): ComposerAttachment | undefined {
  for (const draft of Object.values(useComposerAttachmentsStore.getState().drafts)) {
    const attachment = draft.find((a) => a.id === id);
    if (attachment) return attachment;
  }
  return undefined;
}

function updateAttachment(
  id: string,
  update: (attachment: ComposerAttachment) => ComposerAttachment,
): void {
  useComposerAttachmentsStore.setState((s) => {
    const entry = Object.entries(s.drafts).find(([, draft]) => draft.some((a) => a.id === id));
    if (!entry) return s;
    const [key, draft] = entry;
    return { drafts: { ...s.drafts, [key]: draft.map((a) => (a.id === id ? update(a) : a)) } };
  });
}

function setUploadProgress(id: string, percent: number): void {
  const progress = Math.round(Math.min(100, Math.max(0, percent)));
  const current = findAttachment(id);
  if (current?.status !== 'uploading' || current.progress === progress) return;
  updateAttachment(id, (a) => ({ ...a, progress }));
}

function cancelUpload(id: string): void {
  const queued = uploadQueue.indexOf(id);
  if (queued !== -1) uploadQueue.splice(queued, 1);
  const controller = uploadControllers.get(id);
  if (!controller) return;
  uploadControllers.delete(id);
  controller.abort();
}

async function runUpload(
  attachment: ComposerAttachment,
  upload: Upload,
  controller: AbortController,
): Promise<void> {
  const { id } = attachment;
  const isCurrent = () => uploadControllers.get(id) === controller;
  try {
    const { fileRef } = await upload(attachment.file, {
      signal: controller.signal,
      onProgress: (percent) => {
        if (isCurrent()) setUploadProgress(id, percent);
      },
    });
    if (!isCurrent()) {
      deleteUploadedRef(getChatService(), fileRef);
      return;
    }
    uploadControllers.delete(id);
    updateAttachment(id, (a) => ({
      ...a,
      status: 'ready',
      progress: 100,
      fileRef,
      error: undefined,
    }));
  } catch (err) {
    if (!isCurrent()) return;
    uploadControllers.delete(id);
    const failure = uploadFailure(err);
    updateAttachment(id, (a) => ({ ...a, ...failure }));
  }
  pumpUploads();
}

function startUpload(id: string): void {
  const attachment = findAttachment(id);
  if (attachment?.status !== 'uploading') return;
  const service = getChatService();
  const upload = service.uploadFile?.bind(service);
  if (!upload) {
    updateAttachment(id, (a) => ({ ...a, status: 'ready', progress: 100 }));
    return;
  }
  const controller = new AbortController();
  uploadControllers.set(id, controller);
  void runUpload(attachment, upload, controller);
}

function pumpUploads(): void {
  while (uploadControllers.size < UPLOAD_CONCURRENCY) {
    const next = uploadQueue.shift();
    if (next === undefined) return;
    startUpload(next);
  }
}

export const useComposerAttachmentsStore = create<ComposerAttachmentsState>()((set, get) => ({
  drafts: {},
  texts: {},

  addFiles(key, files, { uploads }) {
    const policy = getAttachmentPolicy();
    const draft = get().drafts[key] ?? [];
    const accepted = files.slice(0, Math.max(0, policy.maxFiles - draft.length));
    const canUpload = uploads && getChatService().uploadFile !== undefined;
    const added = accepted.map((file): ComposerAttachment => {
      const status = rejectionStatus(file, policy) ?? (canUpload ? 'uploading' : 'ready');
      return {
        id: nextAttachmentId(),
        name: file.name,
        size: file.size,
        type: file.type || mimeTypeForExtension(fileExtension(file.name)),
        file,
        status,
        progress: status === 'ready' ? 100 : 0,
      };
    });
    if (added.length > 0) {
      set((s) => ({ drafts: { ...s.drafts, [key]: [...(s.drafts[key] ?? []), ...added] } }));
      for (const attachment of added) {
        if (attachment.status === 'uploading') uploadQueue.push(attachment.id);
      }
      pumpUploads();
    }
    return { dropped: files.length - accepted.length };
  },

  setText(key, text) {
    if ((get().texts[key] ?? '') === text) return;
    set((s) => ({ texts: text ? { ...s.texts, [key]: text } : withoutKey(s.texts, key) }));
  },

  remove(key, id) {
    const removed = get().drafts[key]?.find((a) => a.id === id);
    cancelUpload(id);
    set((s) => {
      const draft = s.drafts[key];
      if (!draft?.some((a) => a.id === id)) return s;
      const rest = draft.filter((a) => a.id !== id);
      return {
        drafts: rest.length > 0 ? { ...s.drafts, [key]: rest } : withoutKey(s.drafts, key),
      };
    });
    pumpUploads();
    if (removed) deleteUploads([removed]);
  },

  retry(key, id) {
    if (get().drafts[key]?.find((a) => a.id === id)?.status !== 'failed') return;
    updateAttachment(id, (a) => ({
      ...a,
      status: 'uploading',
      progress: 0,
      error: undefined,
      refusalCode: undefined,
    }));
    uploadQueue.push(id);
    pumpUploads();
  },

  clear(key) {
    deleteUploads(get().take(key));
  },

  take(key) {
    const { drafts, texts } = get();
    const draft = drafts[key] ?? [];
    if (draft.length === 0 && texts[key] === undefined) return [];
    for (const attachment of draft) cancelUpload(attachment.id);
    set((s) => ({ drafts: withoutKey(s.drafts, key), texts: withoutKey(s.texts, key) }));
    pumpUploads();
    return draft;
  },

  move(fromKey, toKey) {
    set((s) => {
      const files = s.drafts[fromKey];
      const text = s.texts[fromKey];
      const hasDraft = (key: string) => s.drafts[key] !== undefined || s.texts[key] !== undefined;
      if (fromKey === toKey || !hasDraft(fromKey) || hasDraft(toKey)) return s;
      return {
        drafts: files ? { ...withoutKey(s.drafts, fromKey), [toKey]: files } : s.drafts,
        texts: text === undefined ? s.texts : { ...withoutKey(s.texts, fromKey), [toKey]: text },
      };
    });
  },
}));
